// =========================================================================
// Title 画面 (W7)
// =========================================================================
// spec/closing_river_web_mvp_v2.md §「画面フロー」 / §「画面フロー > ランダム
// ボタン枯渇時」
//
// 4 導線:
//   - 「ルールを見る」 → onTutorial
//   - 「main 9 問」    → onMain
//   - 「archive 50 問」→ onArchive
//   - 「ランダム」    → onRandom
//     ※ archive 全完了時は「未達成から選ぶ」 に文言変化 (progress 経由判定)
//
// このモジュールは routing を持たない。callbacks を介して main.ts に委譲する。
// =========================================================================

import { isAllArchiveCompleted } from '../progress.js';

// -------------------------------------------------------------------------
// 公開型
// -------------------------------------------------------------------------

export interface TitleCallbacks {
  onTutorial: () => void;
  onMain: () => void;
  onArchive: () => void;
  onRandom: () => void;
}

export interface TitleScreenHandle {
  mount(container: HTMLElement, callbacks: TitleCallbacks): void;
  destroy(): void;
}

// -------------------------------------------------------------------------
// factory
// -------------------------------------------------------------------------

/**
 * 状態を持たない title 画面を作る。mount で archive id 集合を進捗判定し、
 * ランダムボタンの文言を `archiveBoardIds` 引数で決める。
 *
 * archive id 集合は main.ts が catalog を load した後にしか分からないので、
 * mount に渡せるよう extend した API を別途用意する (`mountWithArchive`)。
 * spec の API は `mount(container, callbacks)` だけだが、archive 枯渇判定は
 * 進捗との突合が必要なので、内部に setArchiveBoardIds() を後付け呼び出し可
 * とする。
 */
export function createTitleScreen(): TitleScreenHandle {
  let root: HTMLElement | null = null;
  let randomBtn: HTMLButtonElement | null = null;
  let archiveBoardIds: readonly string[] = [];
  let mounted = false;

  function refreshRandomButtonLabel(): void {
    if (!randomBtn) return;
    const exhausted = isAllArchiveCompleted(archiveBoardIds);
    const label = exhausted ? '未達成から選ぶ' : 'ランダム';
    const sub = exhausted
      ? 'Silent / Clean が残っている盤面へ'
      : 'archive 未プレイから 1 つ引く';
    randomBtn.dataset['exhausted'] = exhausted ? '1' : '0';
    const labelEl = randomBtn.querySelector<HTMLElement>('[data-role="label"]');
    const subEl = randomBtn.querySelector<HTMLElement>('[data-role="sub"]');
    if (labelEl) labelEl.textContent = label;
    if (subEl) subEl.textContent = sub;
  }

  function build(callbacks: TitleCallbacks): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'nagi-title-root';
    // インラインスタイル: spec の「numbers99 系の静謐ダーク + scale-on-press」
    // styles.css に依存する変数 (--bg, --accent 等) を活用しつつ、レイアウトは
    // local に書く (board-list / tutorial と CSS 共有しなくて良いように)。
    wrapper.style.cssText = [
      'flex: 1',
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'padding: max(env(safe-area-inset-top), 24px) 20px max(env(safe-area-inset-bottom), 32px)',
      'gap: 28px',
      'min-height: 100vh',
      'box-sizing: border-box',
    ].join(';');

    // ---- タイトル ブロック ----
    const titleBlock = document.createElement('header');
    titleBlock.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'gap: 6px',
      'margin-top: 8vh',
    ].join(';');

    const h1 = document.createElement('h1');
    h1.textContent = '閉じる川';
    h1.style.cssText = [
      'margin: 0',
      'font-size: 32px',
      'font-weight: 500',
      'letter-spacing: 0.12em',
      'color: var(--text)',
    ].join(';');

    const subtitle = document.createElement('p');
    subtitle.textContent = '静かに数を閉じる 1D 循環パズル';
    subtitle.style.cssText = [
      'margin: 0',
      'font-size: var(--font-sm)',
      'color: var(--text-dim)',
      'letter-spacing: 0.08em',
    ].join(';');

    const seriesTag = document.createElement('p');
    seriesTag.textContent = 'Nagi #01';
    seriesTag.style.cssText = [
      'margin: 12px 0 0',
      'font-size: 11px',
      'color: var(--accent-dim)',
      'letter-spacing: 0.32em',
      'text-transform: uppercase',
    ].join(';');

    titleBlock.appendChild(h1);
    titleBlock.appendChild(subtitle);
    titleBlock.appendChild(seriesTag);

    // ---- 導線 ボタン ブロック ----
    const menu = document.createElement('nav');
    menu.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'gap: 12px',
      'width: 100%',
      'max-width: 320px',
    ].join(';');

    const tutorialBtn = makeMenuButton({
      label: 'ルールを見る',
      sub: '初回 4 ステップのチュートリアル',
      accent: 'soft',
    });
    tutorialBtn.addEventListener('click', () => {
      callbacks.onTutorial();
    });

    const mainBtn = makeMenuButton({
      label: 'main 9 問',
      sub: '3 難易度 × 3 ・ 終わりが見える',
      accent: 'primary',
    });
    mainBtn.addEventListener('click', () => {
      callbacks.onMain();
    });

    const archiveBtn = makeMenuButton({
      label: 'archive 50 問',
      sub: '5 章 × 10 ・ じっくり腰を据えて',
      accent: 'soft',
    });
    archiveBtn.addEventListener('click', () => {
      callbacks.onArchive();
    });

    const randBtn = makeMenuButton({
      label: 'ランダム',
      sub: 'archive 未プレイから 1 つ引く',
      accent: 'soft',
    });
    randBtn.addEventListener('click', () => {
      callbacks.onRandom();
    });
    randomBtn = randBtn;

    menu.appendChild(tutorialBtn);
    menu.appendChild(mainBtn);
    menu.appendChild(archiveBtn);
    menu.appendChild(randBtn);

    // ---- footer (engine version 等の薄い情報) ----
    const footer = document.createElement('footer');
    footer.style.cssText = [
      'margin-top: auto',
      'padding-top: 24px',
      'font-size: 11px',
      'color: var(--text-dim)',
      'letter-spacing: 0.08em',
      'opacity: 0.7',
    ].join(';');
    footer.textContent = 'closing river / web mvp';

    wrapper.appendChild(titleBlock);
    wrapper.appendChild(menu);
    wrapper.appendChild(footer);

    return wrapper;
  }

  function mount(container: HTMLElement, callbacks: TitleCallbacks): void {
    if (mounted) {
      // 二重 mount は安全に再構築 (callbacks 差し替えのため)
      destroy();
    }
    container.innerHTML = '';
    const node = build(callbacks);
    container.appendChild(node);
    root = node;
    mounted = true;
    refreshRandomButtonLabel();
  }

  function destroy(): void {
    if (root && root.parentNode) {
      root.parentNode.removeChild(root);
    }
    root = null;
    randomBtn = null;
    mounted = false;
  }

  return {
    mount,
    destroy,
    // 内部 API: main.ts から archive id 集合を後から流せるように
    // (TitleScreenHandle 型の外には出さない非公開フィールド)
    // ts には index signature が無いため、as unknown as ... で拡張する形にする。
    ...({
      __setArchiveBoardIds(ids: readonly string[]): void {
        archiveBoardIds = ids;
        refreshRandomButtonLabel();
      },
    } as object),
  } as TitleScreenHandle;
}

// -------------------------------------------------------------------------
// メニューボタン factory
// -------------------------------------------------------------------------

function makeMenuButton(opts: {
  readonly label: string;
  readonly sub: string;
  readonly accent: 'primary' | 'soft';
}): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  // styles.css のグローバル button スタイルを基底に、accent で強弱
  const isPrimary = opts.accent === 'primary';
  btn.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'align-items: flex-start',
    'gap: 4px',
    'padding: 16px 18px',
    'border-radius: var(--radius-md)',
    'background: var(--bg-elev)',
    `border: 1px solid ${isPrimary ? 'var(--accent-dim)' : 'var(--border)'}`,
    'color: var(--text)',
    'text-align: left',
    'width: 100%',
    'cursor: pointer',
    'transition: transform var(--t-fast) ease, background var(--t-fast) ease, border-color var(--t-fast) ease',
  ].join(';');

  const labelEl = document.createElement('span');
  labelEl.dataset['role'] = 'label';
  labelEl.textContent = opts.label;
  labelEl.style.cssText = [
    'font-size: var(--font-lg)',
    'font-weight: 500',
    `color: ${isPrimary ? 'var(--accent)' : 'var(--text)'}`,
    'letter-spacing: 0.04em',
  ].join(';');

  const subEl = document.createElement('span');
  subEl.dataset['role'] = 'sub';
  subEl.textContent = opts.sub;
  subEl.style.cssText = [
    'font-size: var(--font-sm)',
    'color: var(--text-dim)',
    'letter-spacing: 0.02em',
  ].join(';');

  btn.appendChild(labelEl);
  btn.appendChild(subEl);
  return btn;
}

// -------------------------------------------------------------------------
// archive id 集合の流し込み (TitleScreenHandle の拡張 method)
// -------------------------------------------------------------------------

/**
 * main.ts から呼ぶ helper。`createTitleScreen()` が返す handle は内部に
 * `__setArchiveBoardIds` を持っているので、外部からは型安全にこの関数を経由する。
 */
export function setArchiveBoardIds(
  handle: TitleScreenHandle,
  archiveBoardIds: readonly string[],
): void {
  const ext = handle as TitleScreenHandle & {
    __setArchiveBoardIds?: (ids: readonly string[]) => void;
  };
  if (typeof ext.__setArchiveBoardIds === 'function') {
    ext.__setArchiveBoardIds(archiveBoardIds);
  }
}
